import { translateTransactionContentApi, translateTransactionChatApi } from '../util/api';

// ============ ACTION TYPES ============

// Chat Translations
const AI_TRANSLATE_TX_CHAT_REQUEST = 'app/ai/AI_TRANSLATE_TX_CHAT_REQUEST';
const AI_TRANSLATE_TX_CHAT_SUCCESS = 'app/ai/AI_TRANSLATE_TX_CHAT_SUCCESS';
const AI_TRANSLATE_TX_CHAT_ERROR = 'app/ai/AI_TRANSLATE_TX_CHAT_ERROR';

// Activity Feed Translations
const AI_TRANSLATE_ACTIVITY_FEED_REQUEST = 'app/ai/AI_TRANSLATE_ACTIVITY_FEED_REQUEST';
const AI_TRANSLATE_ACTIVITY_FEED_SUCCESS = 'app/ai/AI_TRANSLATE_ACTIVITY_FEED_SUCCESS';
const AI_TRANSLATE_ACTIVITY_FEED_ERROR = 'app/ai/AI_TRANSLATE_ACTIVITY_FEED_ERROR';

// Project Info Translations
const AI_TRANSLATE_PROJECT_INFO_REQUEST = 'app/ai/AI_TRANSLATE_PROJECT_INFO_REQUEST';
const AI_TRANSLATE_PROJECT_INFO_SUCCESS = 'app/ai/AI_TRANSLATE_PROJECT_INFO_SUCCESS';
const AI_TRANSLATE_PROJECT_INFO_ERROR = 'app/ai/AI_TRANSLATE_PROJECT_INFO_ERROR';

// Generic/Custom Translations
const AI_TRANSLATE_CONTENT_REQUEST = 'app/ai/AI_TRANSLATE_CONTENT_REQUEST';
const AI_TRANSLATE_CONTENT_SUCCESS = 'app/ai/AI_TRANSLATE_CONTENT_SUCCESS';
const AI_TRANSLATE_CONTENT_ERROR = 'app/ai/AI_TRANSLATE_CONTENT_ERROR';

// ============ INITIAL STATE ============

const initialState = {
  // Chat translations
  translatingTxChat: false,
  chatResult: null,
  chatError: null,

  // Activity feed translations
  translatingActivityFeed: false,
  activityFeedResult: null,
  activityFeedError: null,

  // Project info translations
  translatingProjectInfo: false,
  projectInfoResult: null,
  projectInfoError: null,

  // Generic translations (by storageKey)
  translatingContent: {},
  contentResults: {},
  contentErrors: {},
};

// ============ REDUCER ============

export default function aiReducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    // Chat translations
    case AI_TRANSLATE_TX_CHAT_REQUEST:
      return { ...state, translatingTxChat: true, chatError: null };

    case AI_TRANSLATE_TX_CHAT_SUCCESS:
      return {
        ...state,
        translatingTxChat: false,
        chatResult: payload || null,
        chatError: null,
      };

    case AI_TRANSLATE_TX_CHAT_ERROR:
      return {
        ...state,
        translatingTxChat: false,
        chatError: payload,
      };

    // Activity feed translations
    case AI_TRANSLATE_ACTIVITY_FEED_REQUEST:
      return { ...state, translatingActivityFeed: true, activityFeedError: null };

    case AI_TRANSLATE_ACTIVITY_FEED_SUCCESS:
      return {
        ...state,
        translatingActivityFeed: false,
        activityFeedResult: payload || null,
        activityFeedError: null,
      };

    case AI_TRANSLATE_ACTIVITY_FEED_ERROR:
      return {
        ...state,
        translatingActivityFeed: false,
        activityFeedError: payload,
      };

    // Project info translations
    case AI_TRANSLATE_PROJECT_INFO_REQUEST:
      return { ...state, translatingProjectInfo: true, projectInfoError: null };

    case AI_TRANSLATE_PROJECT_INFO_SUCCESS:
      return {
        ...state,
        translatingProjectInfo: false,
        projectInfoResult: payload || null,
        projectInfoError: null,
      };

    case AI_TRANSLATE_PROJECT_INFO_ERROR:
      return {
        ...state,
        translatingProjectInfo: false,
        projectInfoError: payload,
      };

    // Generic content translations
    case AI_TRANSLATE_CONTENT_REQUEST:
      return {
        ...state,
        translatingContent: {
          ...state.translatingContent,
          [payload.storageKey]: true,
        },
        contentErrors: {
          ...state.contentErrors,
          [payload.storageKey]: null,
        },
      };

    case AI_TRANSLATE_CONTENT_SUCCESS:
      return {
        ...state,
        translatingContent: {
          ...state.translatingContent,
          [payload.storageKey]: false,
        },
        contentResults: {
          ...state.contentResults,
          [payload.storageKey]: payload.translations,
        },
        contentErrors: {
          ...state.contentErrors,
          [payload.storageKey]: null,
        },
      };

    case AI_TRANSLATE_CONTENT_ERROR:
      return {
        ...state,
        translatingContent: {
          ...state.translatingContent,
          [payload.storageKey]: false,
        },
        contentErrors: {
          ...state.contentErrors,
          [payload.storageKey]: payload.error,
        },
      };

    default:
      return state;
  }
}

// ============ SELECTORS ============

// Chat selectors
export const selectAiTranslatingTxChat = state => state?.ai?.translatingTxChat;
export const selectAiTranslateTxChatResult = state => state?.ai?.chatResult;
export const selectAiTranslateTxChatError = state => state?.ai?.chatError;

// Activity feed selectors
export const selectAiTranslatingActivityFeed = state => state?.ai?.translatingActivityFeed;
export const selectAiActivityFeedResult = state => state?.ai?.activityFeedResult;
export const selectAiActivityFeedError = state => state?.ai?.activityFeedError;

// Project info selectors
export const selectAiTranslatingProjectInfo = state => state?.ai?.translatingProjectInfo;
export const selectAiProjectInfoResult = state => state?.ai?.projectInfoResult;
export const selectAiProjectInfoError = state => state?.ai?.projectInfoError;

// Generic content selectors
export const selectAiTranslatingContent = storageKey => state =>
  state?.ai?.translatingContent?.[storageKey] || false;
export const selectAiContentResult = storageKey => state =>
  state?.ai?.contentResults?.[storageKey] || null;
export const selectAiContentError = storageKey => state =>
  state?.ai?.contentErrors?.[storageKey] || null;

// ============ ACTION CREATORS ============

// Chat actions
export const aiTranslateTxChatRequest = () => ({ type: AI_TRANSLATE_TX_CHAT_REQUEST });
export const aiTranslateTxChatSuccess = payload => ({
  type: AI_TRANSLATE_TX_CHAT_SUCCESS,
  payload,
});
export const aiTranslateTxChatError = e => ({
  type: AI_TRANSLATE_TX_CHAT_ERROR,
  payload: e,
});

// Activity feed actions
export const aiTranslateActivityFeedRequest = () => ({ type: AI_TRANSLATE_ACTIVITY_FEED_REQUEST });
export const aiTranslateActivityFeedSuccess = payload => ({
  type: AI_TRANSLATE_ACTIVITY_FEED_SUCCESS,
  payload,
});
export const aiTranslateActivityFeedError = e => ({
  type: AI_TRANSLATE_ACTIVITY_FEED_ERROR,
  payload: e,
});

// Project info actions
export const aiTranslateProjectInfoRequest = () => ({ type: AI_TRANSLATE_PROJECT_INFO_REQUEST });
export const aiTranslateProjectInfoSuccess = payload => ({
  type: AI_TRANSLATE_PROJECT_INFO_SUCCESS,
  payload,
});
export const aiTranslateProjectInfoError = e => ({
  type: AI_TRANSLATE_PROJECT_INFO_ERROR,
  payload: e,
});

// Generic content actions
export const aiTranslateContentRequest = storageKey => ({
  type: AI_TRANSLATE_CONTENT_REQUEST,
  payload: { storageKey },
});
export const aiTranslateContentSuccess = (storageKey, translations) => ({
  type: AI_TRANSLATE_CONTENT_SUCCESS,
  payload: { storageKey, translations },
});
export const aiTranslateContentError = (storageKey, error) => ({
  type: AI_TRANSLATE_CONTENT_ERROR,
  payload: { storageKey, error },
});

// ============ THUNKS ============

/**
 * AI: Translate + store chat messages for a transaction
 * @param {Object} params
 * @param {string} params.transactionId - Transaction UUID
 * @param {Array<{id: string, text: string}>} params.messages - Messages to translate
 */
export const aiTranslateAndStoreTransactionChat = params => async dispatch => {
  dispatch(aiTranslateTxChatRequest());

  try {
    const res = await translateTransactionChatApi(params);
    dispatch(aiTranslateTxChatSuccess(res));
    return res;
  } catch (e) {
    dispatch(aiTranslateTxChatError(e));
    throw e;
  }
};

/**
 * AI: Translate + store activity feed content (listing titles, user names, etc.)
 * @param {Object} params
 * @param {string} params.transactionId - Transaction UUID
 * @param {Array<{id: string, text: string}>} params.items - Items to translate
 */
export const aiTranslateActivityFeedContent = params => async dispatch => {
  dispatch(aiTranslateActivityFeedRequest());

  try {
    const res = await translateTransactionContentApi({
      transactionId: params.transactionId,
      storageKey: 'activityFeedTranslations',
      items: params.items,
    });
    dispatch(aiTranslateActivityFeedSuccess(res));
    return res;
  } catch (e) {
    dispatch(aiTranslateActivityFeedError(e));
    throw e;
  }
};

/**
 * AI: Translate + store project information
 * @param {Object} params
 * @param {string} params.transactionId - Transaction UUID
 * @param {Array<{id: string, text: string}>} params.items - Items to translate
 */
export const aiTranslateProjectInfo = params => async dispatch => {
  dispatch(aiTranslateProjectInfoRequest());

  try {
    const res = await translateTransactionContentApi({
      transactionId: params.transactionId,
      storageKey: 'projectInfoTranslations',
      items: params.items,
    });
    dispatch(aiTranslateProjectInfoSuccess(res));
    return res;
  } catch (e) {
    dispatch(aiTranslateProjectInfoError(e));
    throw e;
  }
};

/**
 * AI: Universal translate + store any content type
 * @param {Object} params
 * @param {string} params.transactionId - Transaction UUID
 * @param {string} params.storageKey - Where to store in metadata
 * @param {Array<{id: string, text: string}>} params.items - Items to translate
 */
export const aiTranslateTransactionContent = params => async dispatch => {
  const { storageKey } = params;
  dispatch(aiTranslateContentRequest(storageKey));

  try {
    const res = await translateTransactionContentApi(params);
    dispatch(aiTranslateContentSuccess(storageKey, res.translations));
    return res;
  } catch (e) {
    dispatch(aiTranslateContentError(storageKey, e));
    throw e;
  }
};